import React, { useEffect, useState } from 'react';
import EditIcon from '../../assets/EditIcon';
import { IStaffAllocation } from '../../type/staffAllocation';
import DeleteIcon from '../../assets/DeleteIcon';
import DeleteConfirmation from './DeleteConfirmation';
import operationAPI from '../../api/operationApi';
import { toast } from 'react-toastify';
import { translate } from '../../i18n';
import EditIconDark from '../../assets/EditIconDark';
import DeleteIconDark from '../../assets/DeleteIconDark';
import {
  COMPLIANCE_STATUS,
  OPERATION_COMPLIANCE,
} from '../../constants/constant';

interface DriverDetailsProps {
  openModal?: any;
  closeModal?: any;
  updateDriverListOperations?: IStaffAllocation[];
  onOperationStaffIdChange?: (operationStaffId: number) => void;
  onStaffChange: () => void;
}

type CheckboxState = {
  need: boolean;
  kkd: boolean;
  preCheck: boolean;
};

const DriverDetailsCard: React.FC<DriverDetailsProps> = ({
  openModal,
  updateDriverListOperations,
  onOperationStaffIdChange,
  onStaffChange,
}) => {
  const [isModalOpenForDelete, setIsModalOpenForDelete] = useState(false);
  const [selectedOperationStaffId, setSelectedOperationStaffId] = useState<
    number | null
  >(null);

  const [selectedOperationBalloonId, setSelectedOperationBalloonId] = useState<
    number | null
  >(null);

  const openDeleteModal = (
    operationStaffId: number,
    operationBalloonId: number,
  ) => {
    setSelectedOperationStaffId(operationStaffId);
    setSelectedOperationBalloonId(operationBalloonId ?? null);
    setIsModalOpenForDelete(true);
  };

  const closeDeleteModal = () => {
    setSelectedOperationStaffId(null);
    setSelectedOperationBalloonId(null);
    setIsModalOpenForDelete(false);
  };

  const confirmDeletion = async () => {
    try {
      if (!selectedOperationStaffId) {
        console.error('No operationStaffId selected for deletion.');
        return;
      }
      const response = await operationAPI.deleteStaff(
        selectedOperationStaffId,
        selectedOperationBalloonId,
      );
      console.log(response);
      toast.success(translate('driver.deleteStaff'));
      onStaffChange();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.warn(translate('operation.commonError'));
    }
  };

  const [editModes, setEditModes] = useState<boolean[]>(
    (updateDriverListOperations || []).map(() => false), // Initialize with all false
  );

    const [checkboxStates, setCheckboxStates] = useState<CheckboxState[]>([]);
      useEffect(() => {
        if (updateDriverListOperations) {
          setCheckboxStates(
            updateDriverListOperations.map((details) => ({
              need: details?.need || false,
              kkd: details?.kkd || false,
              preCheck: details?.preCheck || false,
            })),
          );
        }
      }, [updateDriverListOperations]);


  const handleCheckboxChange = (
    index: number,
    checkboxName: keyof CheckboxState,
  ) => {
    const updatedCheckboxStates = [...checkboxStates];
    updatedCheckboxStates[index] = {
      ...updatedCheckboxStates[index],
      [checkboxName]: !updatedCheckboxStates[index][checkboxName],
    };
    setCheckboxStates(updatedCheckboxStates);
  };

  const handleOperationStaffIdChange = (
    operationStaffId: number,
    details?: IStaffAllocation,
  ) => {
    if (onOperationStaffIdChange) {
      onOperationStaffIdChange(operationStaffId);
    }
    openModal('DriverForm', details);
  };

  const toggleEditMode = (index: number) => {
    const updatedEditModes = [...editModes];
    updatedEditModes[index] = !updatedEditModes[index]; // Toggle edit mode for the specific card
    setEditModes(updatedEditModes);
  };
  
  return (
    <>
      {updateDriverListOperations?.map((details, index) => {
        return (
          <div className="mt-4" key={details?.operationStaffId}>
            <div
              className={`bg-backgroundColor-white rounded-lg shadow-md p-5`}
            >
              {/* Header */}
              <div className="relative group">
                <div
                  className={`flex justify-between items-center mb-4 rounded ${
                    details?.driverFatigueStyle === COMPLIANCE_STATUS.expired
                      ? 'bg-backgroundColor-fatigueRedColorCode'
                      : details?.driverFatigueStyle ===
                            COMPLIANCE_STATUS.upcoming ||
                          details?.driverFatigueStyle ===
                            COMPLIANCE_STATUS.current
                        ? 'bg-backgroundColor-fatigueYellowColorCode'
                        : 'bg-backgroundColor-white'
                  }`}
                >
                  <h2 className="text-lg font-semibold text-textColor">
                    {details?.staffName}
                  </h2>

                  <div className="flex justify-between">
                    {/* Delete Button */}
                    <button
                      className="text-textColor hover:text-textColor mr-4"
                      onClick={() =>
                        openDeleteModal(
                          details?.operationStaffId,
                          details?.operationBalloonId,
                        )
                      }
                    >
                      <div>
                        <div className="block dark:hidden">
                          <DeleteIcon />
                        </div>
                        <div className="hidden dark:block">
                          <DeleteIconDark />
                        </div>
                      </div>
                    </button>

                    {/* Edit Button */}
                    <button
                      className="text-textColor hover:text-textColor"
                      onClick={() => {
                        toggleEditMode(index);
                        handleOperationStaffIdChange(
                          details?.operationStaffId,
                          details,
                        );
                      }}
                    >
                      <div>
                        <div className="block dark:hidden">
                          <EditIcon />
                        </div>
                        <div className="hidden dark:block">
                          <EditIconDark />
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
                {/* Tooltip */}
                {details?.driverFatigueMessage?.trim() && (
                  <>
                    <div className="absolute z-10 hidden group-hover:block bg-white text-gray-800 text-sm border border-customBorder-border rounded shadow-lg p-2 w-64 top-full left-0 mt-1">
                      <p>{details?.driverFatigueMessage}</p>
                    </div>
                  </>
                )}
              </div>

              {/* Details Section */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-2 text-start text-sm">
                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Start Time</p>
                  <p className="text-textColorGray">
                    {details.startTime
                      ? new Date(details.startTime)
                          .toISOString()
                          .substring(11, 16)
                      : null}
                  </p>
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Route</p>
                  <p className="text-textColorGray">{details?.routeName}</p>
                </div>
                <div className="flex flex-col relative group">
                  <p className="font-medium text-textColorGray">Bus Name</p>
                  <p
                    className={`text-textColorGray px-2 py-1 rounded 
                      ${
                        details?.driverFatigueStyle ===
                        OPERATION_COMPLIANCE.statusExpired
                          ? 'bg-backgroundColor-fatigueRedColorCode'
                          : details?.compliantStyle ===
                                OPERATION_COMPLIANCE.statusUpComing ||
                              details?.compliantStyle ==
                                OPERATION_COMPLIANCE.statusCurrent
                            ? 'bg-backgroundColor-fatigueYellowColorCode'
                            : ''
                      }
                    `}
                  >
                    {details?.busName}
                  </p>

                  {/* Custom Tooltip */}
                  {details?.complianceMessage && (
                    <div className="absolute z-10 hidden group-hover:block bg-white text-gray-800 text-sm border border-customBorder-border rounded shadow-lg p-2 w-48 top-full left-0 mt-1">
                      <p>{details?.complianceMessage}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Balloon</p>
                  <p className="text-textColorGray">{details?.balloonName}</p>
                </div>

                <div className="flex flex-col">
                  <p className="font-medium text-textColorGray">Phone Number</p>
                  <p className="text-textColorGray">
                    {['', 'null', 'undefined', null, undefined].includes(
                      details?.resPhone,
                    )
                      ? '---'
                      : details?.resPhone}
                  </p>
                </div>

                <div className="flex flex-col"></div>

                <div className="flex flex-col">
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                        checked={checkboxStates[index]?.preCheck}
                        onChange={() => handleCheckboxChange(index, 'preCheck')}
                        disabled={!editModes[index]} // Disable the checkbox if not in edit mode
                      />
                      <span className="text-sm text-textBlackColor">
                        Pre Check
                      </span>
                    </label>

                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                        checked={checkboxStates[index]?.need}
                        onChange={() => handleCheckboxChange(index, 'need')}
                        disabled={!editModes[index]} // Disable the checkbox if not in edit mode
                      />
                      <span className="text-sm text-textBlackColor">Need</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-textColor border-gray-300 rounded focus:ring-blue-400"
                        checked={checkboxStates[index]?.kkd}
                        onChange={() => handleCheckboxChange(index, 'kkd')}
                        disabled={!editModes[index]} // Disable the checkbox if not in edit mode
                      />
                      <span className="text-sm text-textBlackColor">KK'D</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Delete Confirmation Modal */}
      {isModalOpenForDelete && (
        <DeleteConfirmation
          isOpen={isModalOpenForDelete}
          onClose={closeDeleteModal}
          onConfirm={confirmDeletion}
          itemName="Driver"
          operationStaffId={selectedOperationStaffId || undefined}
          operationBalloonId={selectedOperationBalloonId || undefined}
        />
      )}
    </>
  );
};

export default DriverDetailsCard;
